import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { CheckSquare, Plus, Gavel, AlertTriangle, Calendar, Shield } from 'lucide-react';

const DECISION_TYPES = ['adjudicator_decision', 'determination', 'daab_decision', 'arbitration_award', 'court_order'];
const COMPLIANCE_COLORS = {
  pending: 'bg-amber-100 text-amber-700', complied: 'bg-green-100 text-green-700',
  partially_complied: 'bg-blue-100 text-blue-700', non_complied: 'bg-red-100 text-red-700',
  challenged: 'bg-purple-100 text-purple-700',
};

export default function DecisionsCompliance({ matterId, profile }) {
  const [decisions, setDecisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    decision_type: 'adjudicator_decision', forum: '', decided_by: '',
    decision_date: '', summary: '', amount_awarded: '', time_awarded_days: '',
    compliance_deadline: '', challenge_deadline: '', notes: '',
  });

  const loadData = useCallback(async () => {
    try {
      const res = await api.disputes.listDecisions(matterId);
      setDecisions(res.data || []);
    } catch { /* silent */ } finally { setLoading(false); }
  }, [matterId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAdd = async () => {
    if (!form.decision_type) { toast.error('Decision type required'); return; }
    try {
      await api.disputes.createDecision(matterId, {
        ...form, matter_id: matterId,
        amount_awarded: form.amount_awarded ? parseFloat(form.amount_awarded) : null,
        time_awarded_days: form.time_awarded_days ? parseInt(form.time_awarded_days) : null,
      });
      toast.success('Decision recorded');
      setShowAdd(false);
      setForm({ decision_type: 'adjudicator_decision', forum: '', decided_by: '', decision_date: '', summary: '', amount_awarded: '', time_awarded_days: '', compliance_deadline: '', challenge_deadline: '', notes: '' });
      loadData();
    } catch { toast.error('Failed to record decision'); }
  };

  const handleComplianceUpdate = async (id, compliance_status) => {
    try {
      await api.disputes.updateDecision(id, { compliance_status });
      toast.success('Compliance updated');
      loadData();
    } catch { toast.error('Update failed'); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-slate-300 border-t-slate-800 rounded-full" /></div>;

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="space-y-4" data-testid="decisions-compliance">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-700">Decisions & Compliance ({decisions.length})</h3>
        <Button size="sm" className="bg-slate-900 hover:bg-slate-800 gap-1 text-xs" onClick={() => setShowAdd(true)} data-testid="add-decision-btn"><Plus className="h-3 w-3" /> Record Decision</Button>
      </div>

      {decisions.length === 0 ? (
        <Card><CardContent className="p-8 text-center"><Gavel className="h-10 w-10 text-slate-300 mx-auto mb-2" /><p className="text-sm text-slate-500">No decisions recorded yet</p></CardContent></Card>
      ) : (
        <div className="space-y-3">
          {decisions.map(d => {
            const challengeOverdue = d.challenge_deadline && d.challenge_deadline < today && d.compliance_status !== 'challenged';
            const complianceOverdue = d.compliance_deadline && d.compliance_deadline < today && d.compliance_status === 'pending';
            return (
              <Card key={d.id} className={complianceOverdue ? 'border-red-200' : ''} data-testid={`decision-${d.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Gavel className="h-5 w-5 text-slate-400 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-[10px] text-slate-400">{d.decision_ref}</span>
                        <Badge variant="outline" className="text-[10px] capitalize">{d.decision_type?.replace(/_/g, ' ')}</Badge>
                        <Badge className={`text-[10px] ${COMPLIANCE_COLORS[d.compliance_status]}`}>{d.compliance_status?.replace(/_/g, ' ')}</Badge>
                      </div>
                      {d.summary && <p className="text-sm text-slate-700 mb-1">{d.summary}</p>}
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        {d.decided_by && <span>By: {d.decided_by}</span>}
                        {d.forum && <span>Forum: {d.forum.replace(/_/g, ' ')}</span>}
                        {d.decision_date && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{d.decision_date}</span>}
                      </div>
                      <div className="flex items-center gap-4 mt-1.5">
                        {d.amount_awarded > 0 && <span className="text-sm font-mono font-bold text-green-700">{new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', maximumFractionDigits: 0 }).format(d.amount_awarded)}</span>}
                        {d.time_awarded_days > 0 && <span className="text-sm text-slate-600">{d.time_awarded_days} days</span>}
                      </div>
                      {/* Deadline warnings */}
                      <div className="flex gap-2 mt-2">
                        {d.compliance_deadline && (
                          <div className={`flex items-center gap-1 text-xs ${complianceOverdue ? 'text-red-600 font-medium' : 'text-slate-400'}`}>
                            <CheckSquare className="h-3 w-3" /> Comply by: {d.compliance_deadline}
                            {complianceOverdue && <AlertTriangle className="h-3 w-3 text-red-500" />}
                          </div>
                        )}
                        {d.challenge_deadline && (
                          <div className={`flex items-center gap-1 text-xs ${challengeOverdue ? 'text-red-600 font-medium' : 'text-slate-400'}`}>
                            <Shield className="h-3 w-3" /> Challenge by: {d.challenge_deadline}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      <Select value="" onValueChange={v => handleComplianceUpdate(d.id, v)}>
                        <SelectTrigger className="h-7 w-[130px] text-xs"><SelectValue placeholder="Compliance" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="complied">Complied</SelectItem>
                          <SelectItem value="partially_complied">Partially</SelectItem>
                          <SelectItem value="non_complied">Non-Complied</SelectItem>
                          <SelectItem value="challenged">Challenged</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="sm:max-w-lg">
          <DialogTitle className="font-barlow font-bold uppercase">Record Decision</DialogTitle>
          <div className="space-y-3 mt-2 max-h-[55vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Decision Type *</Label>
                <Select value={form.decision_type} onValueChange={v => setForm(f => ({...f, decision_type: v}))}>
                  <SelectTrigger data-testid="decision-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{DECISION_TYPES.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Forum</Label>
                <Select value={form.forum} onValueChange={v => setForm(f => ({...f, forum: v}))}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {(profile?.forum_routes || ['adjudication', 'arbitration']).map(r => <SelectItem key={r} value={r}>{r.replace(/_/g, ' ')}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Decided By</Label><Input value={form.decided_by} onChange={e => setForm(f => ({...f, decided_by: e.target.value}))} placeholder="Name of adjudicator / board" /></div>
              <div><Label className="text-xs">Decision Date</Label><Input type="date" value={form.decision_date} onChange={e => setForm(f => ({...f, decision_date: e.target.value}))} /></div>
            </div>
            <div><Label className="text-xs">Summary</Label><Textarea value={form.summary} onChange={e => setForm(f => ({...f, summary: e.target.value}))} rows={3} placeholder="Summarise the decision..." data-testid="decision-summary-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Amount Awarded (ZAR)</Label><Input type="number" value={form.amount_awarded} onChange={e => setForm(f => ({...f, amount_awarded: e.target.value}))} /></div>
              <div><Label className="text-xs">Time Awarded (days)</Label><Input type="number" value={form.time_awarded_days} onChange={e => setForm(f => ({...f, time_awarded_days: e.target.value}))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Compliance Deadline</Label><Input type="date" value={form.compliance_deadline} onChange={e => setForm(f => ({...f, compliance_deadline: e.target.value}))} /></div>
              <div><Label className="text-xs">Challenge Deadline</Label><Input type="date" value={form.challenge_deadline} onChange={e => setForm(f => ({...f, challenge_deadline: e.target.value}))} /></div>
            </div>
            <div><Label className="text-xs">Notes</Label><Textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={handleAdd} data-testid="submit-decision"><Plus className="h-4 w-4 mr-1" /> Record</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
